import {
  AfterContentChecked,
  AfterContentInit,
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  NgZone,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router, RouterModule} from "@angular/router";

import {AccordionModule} from 'ngx-bootstrap/accordion';
import {
  each,
  filter,
  find,
  forEach,
  get,
  includes,
  isArray,
  isEmpty,
  isObject,
  isString,
  set,
  split,
  startsWith,
  toLower,
  toUpper
} from 'lodash-es';
import {
  AppManagerService,
  AppRef,
  AppResourceManagerService,
  CanvasDomService,
  ComponentRefProvider,
  ComponentRefProviderService,
  DesignContainerComponent,
  PageViewsManager,
  PipeProvider,
  RegisterWidgetProps,
  WmsDraggableDirective
} from "@wms/design-container";
import {
  DialogService,
  FileService,
  NotificationService,
  PageMeta,
  PageService,
  PageTemplatesService,
  PreferenceService,
  ServiceFactoryService,
  StudioFactory,
  StudioService,
  ThemeService,
  TreeService
} from '@wms/services';
import {
  AddVariableConfigComponent,
  BaseVariableService,
  VariableConfigComponent,
  VariableCrudManagerService
} from '@wms/variables';
import {
  getProjectResourcePath,
  getVcsAccountIconUrl,
  getVCSGroupData,
  hasTokenOverrideChanges,
  loadStyleSheet,
  MarkupManagerService,
  removeShadowdom,
  StudioUtils,
  toPascalCase,
  triggerFn,
  VersionManagerPipe
} from "@wms/core";
import {
  BaseComponent,
  BindVariableTreeComponent,
  GlobalConfirmDialogComponent,
  IWMSStandaloneEditorConstructionOptions,
  RenamePageComponent,
  SearchComponent,
  WmsDialogBodyComponent,
  WmsDialogComponent,
  WmsDialogFooterComponent,
  WmsDialogHeaderComponent,
  WMSEditorComponent,
  WmsSpinnerComponent,
  WmsSplitPaneComponent,
  WmsSplitPanelComponent,
} from '@wms/components';
import {WmsFeatureDirective} from "@wms/directives";
import {Parent} from "@wms/app/parent";
import {PageDetails, PageLayoutsService, PageStructureComponent, WsManager} from "@wms/page-workspace";
import {App, isDefined, runtimeEventManager, setNgZone, setPipeProvider, stringStartsWith} from "@wm/core";
import {BsDropdownDirective, BsDropdownModule} from "ngx-bootstrap/dropdown";
import {PageWsService} from "./pageWsService";
import {clearWidgetRegistry, FilterPipe, getWidgetRegistry, OrderByPipe, WmComponentsModule} from "@wm/components-base";
import {PrefabManager} from "./prefabs/prefab-manager";
import {PropertiesPanelComponent, PropertyTemplatesListComponent, WmsWidgetHelpDirective} from '@wms/properties-panel';
import {environment} from "@wms/app";
import {editor, IPosition} from "monaco-editor";
import {WidgetTemplatesService} from '@wms/app/design-container/services/widget-templates.service';
import {WmsWorkspaceManagerService} from "@wms/workspace";
import {UpgradePrefabComponent} from '@wms/app/prefab-publish/upgrade-prefab/upgrade-prefab.component';
import {TemplateService} from "@wms/app/properties-panel/properties-template/template-service";
import {ProjectUpdatesService} from "@wms/app/project-updates/services/project-updates/project-updates.service";
import {ZoomService} from './services/zoom.service';
import {monacoThemes} from '@wms/app/components/wms-editor/monacoThemes';
import {SecurityService} from "@wms/settings/security";
import {VcsService} from "@wms/app/vcs/services/vcs.service";
import ITextModel = editor.ITextModel;
import FindMatch = editor.FindMatch;
import {TabsModule} from "ngx-bootstrap/tabs";

interface PagesList {
  name: string;
  type: string;
  params: Array<any>;
}


let activePageName: string = '';
let TABS = {
  'DESIGN': 'DESIGN',
  'MARKUP': 'MARKUP',
  'SCRIPT': 'SCRIPT',
  'STYLE': 'STYLE',
  'VARIABLES': 'VARIABLES'
};
const ACTIONS = {
  'ADD': 1,
  'DELETE': 2
};

@Component({
  standalone: true,
  selector: 'wms-page-workspace',
  templateUrl: './page-workspace.component.html',
  styleUrls: ['./page-workspace.component.less'],
  imports: [
    CommonModule,
    WmsDialogComponent,
    VariableConfigComponent,
    SearchComponent,
    WmsSplitPanelComponent,
    WmsSplitPaneComponent,
    AccordionModule,
    RouterModule,
    // WmsTreeComponent,
    WmsDraggableDirective,
    WmsFeatureDirective,
    PageStructureComponent,
    WmsSpinnerComponent,
    WMSEditorComponent,
    BsDropdownModule,
    WmComponentsModule,
    FilterPipe,
    OrderByPipe,
    DesignContainerComponent,
    BindVariableTreeComponent,
    AddVariableConfigComponent,
    RenamePageComponent,
    GlobalConfirmDialogComponent,
    VersionManagerPipe,
    WmsWidgetHelpDirective,
    UpgradePrefabComponent,
    PropertyTemplatesListComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsDialogHeaderComponent,
    TabsModule
  ],
  providers: [
    AppResourceManagerService,
    AppManagerService,
    ComponentRefProviderService,
    WsManager,
    PageLayoutsService,
    PageViewsManager,
    { provide: ComponentRefProvider, useClass: ComponentRefProviderService },
    { provide: Parent, useExisting: forwardRef(() => PageWorkspaceComponent) },
    PropertiesPanelComponent,
    TemplateService,
    { provide: App, useClass: AppRef },
  ],
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA]
})
export class PageWorkspaceComponent extends BaseComponent implements AfterViewInit, AfterContentInit, AfterContentChecked, OnInit, OnDestroy {
  @ViewChild('dynamicComponent', { read: ViewContainerRef }) dynamicComponentContainerRef: ViewContainerRef | undefined;
  @ViewChild(DesignContainerComponent) designContainerComp: DesignContainerComponent | undefined;
  @ViewChild(SearchComponent) searchComponent: SearchComponent;
  @ViewChild(BsDropdownDirective) protected bsDropdown: any;
  private editorComponent: WMSEditorComponent;
  private preferenceService: PreferenceService;
  protected pageWsService: PageWsService;
  notificationService: NotificationService;
  private lineComponent: { x: number; y: number };
  private PageViewsManager: PageViewsManager;
  protected readonly TABS = TABS;
  private wsManager: WmsWorkspaceManagerService;
  private securityService: SecurityService;
  public showCanvasFitScreen: boolean;
  // groupThemes: GroupThemes[] = [];

  @ViewChild(WMSEditorComponent, { static: false }) set content(content: WMSEditorComponent) {
    // TODO: check if this is required
    if (content) {
      this.editorComponent = content;
      this.pageWsService.editor = content;
    }
  };
  @Output('dragStart') dragStratEventEmitter = new EventEmitter(true);
  @Output('dragEnd') dragEndEventEmitter = new EventEmitter(true);

  dialogParams: any;
  caption: any;
  messageContent: any;
  deleteParams: any;

  baseURL: string;
  @Output() isPrefabTemplate: boolean;
  isTemplateBundleType = false;
  pageActionsDD: any = [];
  pageInfo: any = {};
  hideProperties: boolean = false;
  fitScreen: boolean = false;
  activeTab: string = "DESIGN";
  activeWidget: any = {};
  variables: any;
  widgetGroup: any = {};
  filteredWidgetGroup: any;
  pagesGroup: { open: boolean } = {
    open: false
  };
  bindingsFound: any;
  widgetFactory: any = [];
  // TODO: move the widgetGroup data to a service
  widgetsTreePane: { open: boolean } = {
    open: false
  };
  variablesTreePane: { open: boolean } = {
    open: false
  };
  pageMinJSON: any;

  //TODO: to remove all below props
  template: any = {};
  layout: any = {};
  activeLayout: any = {}
  page: any = {};
  group: any = {};
  $index: any;
  subGroup: any = {};
  widget: any = {};
  app: any;
  availableDevices: any;

  @ViewChild(WmsDraggableDirective) draggable: WmsDraggableDirective | undefined;
  public showCreateNewPage: boolean = false;
  private defaultDetails = {
    pageName: 'Main',
    tab: 'DESIGN',
    iconClass: 'wms wms-page'
  }
  private widgetsPropsProvider: RegisterWidgetProps;
  pagesService: PageService;
  templateOptions: any;
  layoutOptions: any;
  pages: any;
  pageParams: any;
  // newParam: any;
  fileService: FileService;
  liveWidgetUtilsService: any;
  CONSTANTS = {
    EXCLUDED_PAGES_BY_FEATURE: {
      DELETE: ['Main', 'Common', 'Login', 'footer', 'header', 'rightnav', 'leftnav', 'topnav'],
      RENAME: ['Main', 'Common', 'Login', 'footer', 'header', 'rightnav', 'leftnav', 'topnav']
    }
  }
  templateSupportedWidgets = ["wm-breadcrumb", "wm-wizard"];

  // TODO: modify this
  isPrefabType: boolean;
  MAIN_PAGE = '';
  templateMenu: any = { show: this.showTemplateMenu.bind(this), visible: false };
  backgroundPositions: any;
  private activeTheme: any;
  protected isNativeMobileApplicationType: boolean;
  protected isMobileApplicationType: boolean;
  public enablePageStructureActiveNode: boolean = true;
  protected isSilo: boolean;
  searchText: string;
  searchTextPre: string;
  templateData:any;
  public projectUpdatesService: ProjectUpdatesService;
  variableCrudManager:VariableCrudManagerService;
  isWSSwitchBlocked: boolean;
  selectedWidgetsCount: number = 0;
  editorLanguageType:string;
  vcsGroupName: string;
  vcsGroupLabel: string;
  vcsRepoType: string

  constructor(inj: Injector, private ngZone: NgZone, private router: Router,
    private route: ActivatedRoute,
    private appRef: AppRef,
    private markupManager: MarkupManagerService,
    public componentRefProvider: ComponentRefProvider,
    _pipeProvider: PipeProvider,
    private dialogService: DialogService,
    public variableService: BaseVariableService,
    private treeService: TreeService,
    private elRef: ElementRef,
    private themeService: ThemeService,
    private widgetTemplatesService: WidgetTemplatesService,
    private pageTemplate: PageTemplatesService,
    public prefabManager: PrefabManager,
    public canvasDomService: CanvasDomService,
    private serviceFactory: ServiceFactoryService,
    private resourceMngr: AppResourceManagerService,
    private appManager: AppManagerService,
    public studioService: StudioService,
    private studioFactory: StudioFactory,
    public zoomService: ZoomService,
    private vcsService: VcsService
) {
    super();
    this.baseURL = environment.SERVICES_BASE_PATH;
    if (this.projectService.isDesignTokensEnabled
      && this.projectService.isNativeMobileApplicationType()) {
      this.themeService.loadMobileFoundationTheme();
    } else if(!this.projectService.isPrism() && !this.projectService.isWebApplicationType() && !this.projectService.isPrefabType()) {
      this.themeService.loadActiveTheme();
    }
    this.preferenceService = inject(PreferenceService);
    this.pagesService = inject(PageService);
    this.pageWsService = inject(PageWsService);
    this.PageViewsManager = inject(PageViewsManager);
    this.fileService = inject(FileService);
    this.notificationService = inject(NotificationService);
    this.wsManager = inject(WmsWorkspaceManagerService);
    this.securityService = inject(SecurityService);
    this.studioFactory = inject(StudioFactory);
    this.projectUpdatesService = inject(ProjectUpdatesService);
    this.activeTheme = this.projectService.getActiveTheme();
    this.variableCrudManager = inject(VariableCrudManagerService);
    setPipeProvider(_pipeProvider);
    setNgZone(ngZone);


    // page widgets context can be fetched outside the page workspace. In those cases widgets registry should be cleared.
    clearWidgetRegistry();

    this.isPrefabTemplate = this.isPrefabType = this.appRef.isPrefabType;
    this.isNativeMobileApplicationType = this.projectService.isNativeMobileApplicationType();
    this.isMobileApplicationType = this.projectService.isMobileApplicationType();

    this.pageWsService.loadAvailableDeviceTypes();

    this.registerDestroyListener.add(this.appManager.subscribe('pageReady', () => {
      this.postWSLoad();
      //update the hidden elements on page ready basis of showHiddenElements value in the storage
      this.pageWsService.showHiddenElements = !this.pageWsService.showHiddenElements;
      this.toggleHiddenElements();
      setTimeout(() => {
        runtimeEventManager.emit('page-ws-ready');
      });
    }));

    this.eventManager.updatePageDataObservable().subscribe((data: any) => {
      let tab = get(data, 'tab');
      this.setEditorOptions(tab);
    });

    this.processStudioRefreshQueue();

    this.eventManager.onTabswitchObservable().subscribe((data: any) => {
      this.switchTab(data.tab, data.details);
    });

    this.registerDestroyListener.add(this.themeService.switchThemeObservable().subscribe((theme: any) => {
      let selectedThemeName = theme.name;
      this.activeTheme = selectedThemeName;
      if ((this.isMobileApplicationType ||this.isNativeMobileApplicationType) && this.pageWsService.selectedViewPort.os) {
        selectedThemeName += '/' + this.pageWsService.selectedViewPort.os;
      }
      document.querySelectorAll('[theme=wmtheme]')[0].remove()
      const themePath = getProjectResourcePath(this.projectService.getProjectId()) + 'themes/' + selectedThemeName + '/style.css';
      loadStyleSheet(themePath, { name: "theme", value: "wmtheme" });
    }));

    this.registerDestroyListener.add(treeService.pagesListUpdate.subscribe((response: PageMeta[]) => {
      this.loadPages(response);
    }));

    this.registerDestroyListener.add(this.eventManager.pageMarkupObservable().subscribe((data: any) => {
      this.pageMinJSON = data;
    }));

    this.registerDestroyListener.add(this.eventManager.updatePageMinJsonObservable().subscribe((data: any) => {
      this.pageMinJSON[data.type] = data.value;
    }));

    runtimeEventManager.on('loadPage').subscribe((pageInfo: any) => {
      if (!pageInfo) {
        return;
      }
      if (isString(pageInfo)) {
        this.selectPage(pageInfo);
      } else {
        if(this.pageWsService.pageSubCategory !== 'BUILD'){
          this.pageWsService.pageSubCategory= 'BUILD'
        }
        this.selectPage(pageInfo.pageName, pageInfo.tab, pageInfo.curPos, pageInfo.isTemplate);
      }
    });


    runtimeEventManager.on('collapse').subscribe(() => {
      this.pageWsService.handelExpaindCollapse = true
      this.pageWsService.toggleToolsPanelDisplay()
    });


    // This is triggered to load or update the design. Dom rendering happens on variable update.
    runtimeEventManager.on('loadDesign').subscribe((pageName: string) => {
      this.pageWsService.markupUpdated = true;
      this.switchTab(TABS.DESIGN, { pageName: pageName });
    });

    runtimeEventManager.on('add-event-handler').subscribe((data) => {
      this.eventHandler(data.event, data.options, data.isVariableEvt);
    });
    runtimeEventManager.on('save-variables').subscribe((data) => {
    this.variableCrudManager.saveAndClose(true,true,true);
    });

    runtimeEventManager.on('update-resource-content').subscribe((data) => {
      this.writeContent(data);
    });

    runtimeEventManager.on('load-widget-template').subscribe((data) => {
      this.loadWidgetTemplates(data);
    });

    runtimeEventManager.on('navigateToScript').subscribe((data) => {
      this.postPageScriptUpdate(data);
    });

    runtimeEventManager.on('show-toaster').subscribe((data) => {
      this.notificationService.show(data.type, data.title, data.desc);
    });

    this.registerDestroyListener.add(runtimeEventManager.on('on-undo-shortcut').subscribe(() => this.undo()));
    this.registerDestroyListener.add(runtimeEventManager.on('on-redo-shortcut').subscribe(() => this.redo()));

    this.registerDestroyListener.add(this.eventManager.widgetSelectionCountObservable().subscribe((count) => {
      this.selectedWidgetsCount = count;
    }));

    let pages = this.pageWsService.projectService.getDetails().pages;
    if (pages) {
      this.loadPages(pages);
    }
   this.eventManager.onEditPageName().subscribe((data: any) => {
     this._editPageName(data.pageName,data.pageType);
   })
    this.eventManager.onDeletePage().subscribe((data: any) => {
      this._deletePage(data.pageName,data.pageType);
    })

    this.eventManager.onPageDuplicate().subscribe((data: any) => {
      this._duplicatePage(data.pageName,data.pageType);
    })
    this.vcsRepoType = toPascalCase(this.projectService.projectEdnDetails.vcsAccountDTO.vcsAccountType)
  }

  override refreshStudio(changeSet: any): boolean {
    let isPage = this.isPage(changeSet);
    let isRefreshDone = false;
    if(isPage) {
      isRefreshDone = true;
      let pageName = this.getPageName(changeSet);
      let isPageCreatedOrDeleted = this.isPageCreatedOrDeleted(changeSet);
      this.securityService.getAuthInfo();
      this.wsManager.setDirtyonWs(false);
      this.resourceMngr.removeUrlCache(pageName);
      this.treeService.updatePagesTree();
      if(isPageCreatedOrDeleted === "ADDED" || isPageCreatedOrDeleted === "MODIFIED") {
        setTimeout(() => {
          this.selectPage(pageName, TABS.DESIGN, undefined, false);
          StudioUtils.updateAppVariables(pageName, null, null);
        }, 0);
      }

      if(isPageCreatedOrDeleted === "DELETED") {
        setTimeout(() => {
          this.eventManager.emitReRenderCanvasContent({ pageName: "Main", isMarkupUpdated: true });
          runtimeEventManager.emit('loadPage', "Main");

          this.eventManager.emitUpdatePageTree();
          this.eventManager.emitupdateRouteHistory({name: pageName, type: "PAGE"});

          this.variableService.deleteVariable('goToPage_' + pageName);
          this.variableService.deleteVariable('goToPage-' + pageName);
          this.variableService.deleteVariable("", pageName);
          this.resourceMngr.removeUrlCache(pageName);
        }, 0)
      }
    }
    // Checking if any design token (overrides) files have changed in this change set
    const hasTokenChanges = hasTokenOverrideChanges(changeSet);
    if (hasTokenChanges) {
      isRefreshDone = true;
      setTimeout(() => {
        this.eventManager.emitReloadDesignTokens();
      }, 0);
    }

    return isRefreshDone;
  }

  getPageName(changeSet: any): string {
    let listOfChangesObj = changeSet.mergedFiles;
    for (const fileName in listOfChangesObj) {
      if(fileName.indexOf("src/main/webapp/pages") !== -1) {
        return fileName.split("src/main/webapp/pages/")[1].split("/")[0];
      }
    }
    return "";
  }

  isPage(changeSet: any) {
    let listOfChangesObj = changeSet?.mergedFiles;
    let isPage = false;
    if(listOfChangesObj) {
      for (const fileName in listOfChangesObj) {
        if(fileName.indexOf("src/main/webapp/pages") !== -1) {
          isPage = true;
          return isPage;
        }
      }
    }
    return isPage;
  }

  isPageCreatedOrDeleted(changeSet: any) {
    let listOfChangesObj = changeSet?.mergedFiles;
    let pageModificationFlag = "ADDED";
    if(listOfChangesObj) {
      for (const fileName in listOfChangesObj) {
        if(fileName.indexOf("src/main/webapp/pages") !== -1) {
          pageModificationFlag = listOfChangesObj[fileName]
          return pageModificationFlag;
        }
      }
    }
    return pageModificationFlag;
  }

  get activePageInfo() {
    return this.pageWsService.activePageInfo || {};
  }

  private formatThemeName(key: string): string {
    // Convert camelCase to Title Case with spaces
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
      .replace(/\s+/g, ' ');
  }

  private formatThemeValue(key: string): string {
    // Convert camelCase to kebab-case
    return key
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '');
  }

  loadPages(pages?: Array<PageMeta>) {
    pages = pages || this.pageWsService.projectService.getDetails().pages;
    if (pages) {
      this.pages = pages;
      this.dispatchSafeEvent('logPagesInfo',
        pages
        .filter(i => i.type === 'PAGE')
        .map(i => i.name)
      );
      this.pages.forEach(() => {
        this.pageActionsDD.push(false);
      })
    }
  }

  ngOnInit() {
    const brightThemes = Object.entries(monacoThemes)
      .filter(([key]) => ['chrome', 'dreamweaver', 'eclipse', 'xcode', 'sqlserver'].includes(key))
      .map(([key, _]) => ({
        name: this.formatThemeName(key),
        value: this.formatThemeValue(key)
      }));

    const darkThemes = Object.entries(monacoThemes)
      .filter(([key]) => ['chaos', 'idleFingers', 'krTheme', 'merbivore', 'monokai', 'twilight', 'vibrantInk'].includes(key))
      .map(([key, _]) => ({
        name: this.formatThemeName(key),
        value: this.formatThemeValue(key)
      }));

    this.pageWsService.groupThemes = [
      {
        label: 'Default',
        themes: [
          { name: 'Visual Studio', value: 'vs' },
          { name: 'Visual Studio Dark', value: 'vs-dark' }
        ]
      },
      {
        label: 'Bright',
        themes: brightThemes
      },
      {
        label: 'Dark',
        themes: darkThemes
      }
    ];
    this.studioService.getOrganizationsList().then(() => {
      this.isSilo = this.studioService.isSilo;
    });
    this.wsManager.setActiveWSCategory('PAGE');
    this.MAIN_PAGE = this.pageWsService.activePageName;
    document.addEventListener("keydown", this.handleKeyboardResourceSave);
    this.backgroundPositions = this.widgetTemplatesService.getBackgroundPositions();
    this.liveWidgetUtilsService = runtimeEventManager.getService('LiveWidgetUtilsService');
    this.pageWsService.setActiveWs(this.pageWsService.activePageName);
    this.projectUpdatesService.isInvalidRemoteRepo.subscribe((invalidRepoBranchMessage: string) => {
      this.openDialogForInvalidRepoBranch(invalidRepoBranchMessage);
    });
    this.pageWsService.isActivetabDesign = 'DESIGN';
    this.pageWsService.activeTab = 'DESIGN'
    this.pageWsService.selectedTab = 'MARKUP';
  }

  ngAfterViewInit() {
    this.zoomService.initializeZoom(
      '.canvas-content',
      '.page-design-container'
    );
    this.pageWsService.editorOptions.readOnly = !this.getPreference('project.pages.edit');
    this.ngZone.run(() => {
      set(this.componentRefProvider, 'app.dynamicComponentContainerRef', this.dynamicComponentContainerRef);
      this.router.navigate([this.pageWsService.activePageName], { relativeTo: this.route });
    });

    /* If the project not of type prism but is of applcation type then inject the app.css globally.
    TODO: Remove app.css as a global style */
    if (((!this.projectService.isPrism() && this.projectService.isApplicationType()) && !this.projectService.isWebApplicationType())
      || this.projectService.isNativeMobileApplicationType())  {
      let appCssPath = environment.SERVICES_BASE_PATH + 'services/projects/' + this.projectService.getProjectId()
        + '/resources/content/'
        + (this.projectService.isNativeMobileApplicationType() ? 'project/generated-rn-app/designtime/': 'web/')
        + 'app.css';
      loadStyleSheet(appCssPath);
    }
    if(!this.projectService.getDetails().mergeInProgress) {
      this.projectUpdatesService.init()
    }
  }

  ngAfterViewChecked(): void {
    if (!this.pageWsService.editorOptions.theme || !this.pageWsService.editorOptions.fontSize) {
      this.pageWsService.editorOptions.theme = this.editorComponent.options.theme;
      this.pageWsService.editorOptions.fontSize = this.editorComponent.options.fontSize;
    }
  }

  ngAfterContentInit() {
    this.templateOptions = [];
    this.pageWsService.layoutOptions = [];
  }

  ngAfterContentChecked() {
    this.pageWsService.isDirty();
  }

  blockWSSwitch() {
    this.isWSSwitchBlocked = true;
  }

  unblockWSSwitch() {
    this.isWSSwitchBlocked = false;
  }

  postWSLoad() {
    if (!this.studioFactory.hasMergeChangesConflicts) {
      // update variables tree
      this.eventManager.emitUpdateVariableTree();
    }
    let footerStatus= $(".workspace-content .canvas-container .canvas-panel.view-port-web");
    if(footerStatus.find('.app-footer').css("display") === 'none'){
      footerStatus.find('aside.app-left-panel').css('border-radius', '0 0 0 14px');
      footerStatus.find('.app-page-content').css('border-radius', '0 0 14px 0');
      footerStatus.find('aside.app-right-panel').css('border-radius', '0 0 14px 0');
    }
  }

  closeDialogWithId(id: string): void {
    this.dialogService.close(id)
  }

  openDialogWithId(id: string): void {
    this.dialogService.open(id)
  }

  filterPages(event: any) {
    this.pageWsService.pageSearchKey = event.searchKey;
  }
  filterWidget(group :any, event:any){
    this.searchText = event.searchKey
    this.filter(group, this.searchText);

  }
  filterPrefab(group :any, event:any){
    this.searchTextPre = event.searchKey
    this.filter(group, this.searchTextPre);
  }

  filter(group: any, searchKey: string): void {
    searchKey = toLower(searchKey)
    group.subGroups.forEach((subGroup: any) => {
      if (!subGroup.hide) {
        if (searchKey) {
          subGroup.filteredWidgets = subGroup.widgets.filter((filteredWidget: any) => {
            if (searchKey.length <= 1) {
              return (toLower(filteredWidget?.name).indexOf(searchKey) === 0);
            } else {
              let searchKeywords = (keywords: string, searchKey: string) => {
                if (!keywords) {
                  return false;
                }
                return find(split(keywords, ","), (keyword) => {
                  return includes(toLower(keyword).trim(), searchKey);
                });
              }
              return includes(toLower(filteredWidget?.searchName), searchKey) || includes(toLower(filteredWidget?.displayName), searchKey) || searchKeywords(filteredWidget?.keywords, searchKey) || includes(toLower(filteredWidget?.name), searchKey);
            }
          });
        } else {
          subGroup.filteredWidgets = subGroup.widgets;
        }
      }
    });
  }

  async setEditorOptions(tab?: string): Promise<void> {
    let value: any;
    let renderLineHighlight: string = 'none'
    tab = tab || this.activeTab;
    switch (tab) {
      case TABS.DESIGN:
        value = '';
        this.pageWsService.editorOptions = { ...this.pageWsService.editorOptions, value: "" };
        break;
      case TABS.MARKUP:
        function removeWizardAction(xmlString: string) {
          const regex = /<wm-wizardaction\b[^>]*>[\s\S]*?<\/wm-wizardaction>/g;
          return xmlString.replace(regex, '');
        }
        value = this.projectService.isMobileApplicationType() ? removeWizardAction(this.pageMinJSON.html) : this.pageMinJSON.html;
        renderLineHighlight = 'all'
        this.pageWsService.editorOptions = { ...this.pageWsService.editorOptions, value: value, language: "html" , }
        this.editorLanguageType  = '.html'
        break;
      case TABS.SCRIPT:
        value = this.pageMinJSON.script
        this.pageWsService.editorOptions = { ...this.pageWsService.editorOptions, value: value, language: "javascript" }
        this.editorLanguageType  = '.js'
        break;
      case TABS.STYLE:
        value = this.pageMinJSON.style;
        this.pageWsService.editorOptions = { ...this.pageWsService.editorOptions, value: value, language: "css" }
        this.editorLanguageType  = '.css'
        break;
    }
    await this.editorComponent.triggerFormat(value);
    this.editorComponent.setrenderLineHighlight(renderLineHighlight)
    this.pageWsService.saveActiveTabContent();
  }


  switchTab(activeTab: string, details: PageDetails) {
    this.blockWSSwitch();
    const previousTab = this.activeTab;
    const activeWidgetName = this.activeWidget?.name
    this.activeWidget = null;
    return this.pageWsService.switchTab(activeTab, details).then((err) => {
      this.unblockWSSwitch();
      this.pageWsService.savedEditorContent = '';
      if(activeTab === this.TABS.DESIGN){
        this.pageWsService.isActivetabDesign = 'DESIGN'
      }else{
        this.pageWsService.isActivetabDesign = '';
        this.pageWsService.selectedTab = activeTab;
      }
      if(this.pageWsService.isActivetabDesign !== 'DESIGN'){
        this.pageWsService.pageSubCategory = 'STRUCTURE'
      }
      this.activeTab = activeTab
      this.eventManager.emitchangeInActiveTab(activeTab)
       // Only restore widget selection when switching from Markup to Design. When switching from Script/Style,
       // the .active-tree-element is stale (from a previous Markup session) and would incorrectly select a markup widget.
       if(activeTab === TABS.DESIGN && previousTab === TABS.MARKUP){
         const activeMarkupWidgetId = $('.active-tree-element').attr('widgetid');
         if (activeMarkupWidgetId) {
           setTimeout(() => {
            this.eventManager.emitOnWidgetSelection({
              'eventType' : 'SELECT',
              'widgetId' : activeMarkupWidgetId
            });
          });
         }
       }
    }, (err) => {
      this.unblockWSSwitch();
    });
  }

  save(): Promise<any> {
    return this.pageWsService.saveActiveTabContent();
  }

  selectPage(pageName: string,activeTab?: any, curPos?: any, isTemplate?: boolean) {
    let tab  = activeTab ? activeTab : TABS.DESIGN;
    this.blockWSSwitch();
    this.switchTab(tab, { pageName: pageName, tab: tab, isTemplate: isTemplate }).then((err: any) => {
      this.unblockWSSwitch();
      if (pageName !== this.pageWsService.activePageName)
        this.variableService.unload(this.pageWsService.activePageName);
        this.pageWsService.activePageName = pageName;
        this.pagesService.activePageName = pageName;
        this.router.navigate([pageName], { relativeTo: this.route });
        if(curPos){
          setTimeout(() => {
            this.setCursorPosition(curPos);
          });
        }
      }, (err) => {
      this.unblockWSSwitch();
    });
  }

  displayRename(pageName: string) {
    return !includes(this.CONSTANTS.EXCLUDED_PAGES_BY_FEATURE.RENAME, pageName)
  }

  displayDelete(pageName: string) {
    return !includes(this.CONSTANTS.EXCLUDED_PAGES_BY_FEATURE.DELETE, pageName);
  }

  _editPageName(pageName: any, pageType: any) {
    let caption = '',
      content = '';

    if (this.isTemplateBundleType) {
      caption = 'LABEL_CONFIRM_RENAME_TEMPLATE';
      content = this.locale.MESSAGE_RENAME_TEMPLATE;
    } else {
      if (pageType !== 'PAGE') { // partial
        caption = 'LABEL_CONFIRM_RENAME_PARTIAL_PAGE';
        content = this.locale.MESSAGE_RENAME_PARTIAL_PAGE;
      } else {
        caption = 'LABEL_CONFIRM_RENAME_PAGE';
        content = this.locale.MESSAGE_RENAME_PAGE;
      }
    }
    this.dialogParams = {
      dialogCaption: caption,
      dialogContent: content,
      pageName: pageName,
      pageType: pageType,
      action: 'RENAME'
    };
    this.openDialogWithId('renamePageDialog');
  }

  editPageName(event: any, pageName: string, pageType: string) {
    event.preventDefault();
    this.hideDropDowns();
    this._editPageName(pageName, pageType);
  }

  deletePage(event: any, pageName: string, pageType: string) {
    event.preventDefault();
    this.hideDropDowns();
    this._deletePage(pageName, pageType);
  }

  hideDropDowns() {
    forEach(this.pageActionsDD, (val: any, key: any) => {
      if (this.pageActionsDD[key]) {
        this.pageActionsDD[key] = !this.pageActionsDD[key];
      }
    });
    this.leftPanelMoreActionsDD = false;
    this.headerExportDD = false;
    this.headerSettingsDD = false;
    this.headerDeviceBuildDD = false;
    this.headerDeployDD = false;
    this.headerVCSDD = false;
    this.headerRecentWSDD = false;
    this.headerRunDD = false;
  }

  _deletePage(pageName: any, pageType: any) {
    let caption = '',
      content = '';

    if (this.isTemplateBundleType) {
      caption = 'LABEL_CONFIRM_DELETE_TEMPLATE';
      content = this.locale.MESSAGE_DELETE_TEMPLATE;
    } else {
      if (pageType !== 'PAGE') { // partial
        caption = 'LABEL_CONFIRM_DELETE_PARTIAL_PAGE';
        content = this.locale.MESSAGE_DELETE_PARTIAL_PAGE;
      } else {
        caption = 'LABEL_CONFIRM_DELETE_PAGE';
        content = this.locale.MESSAGE_DELETE_PAGE;
      }
    }

    this.caption = caption;
    this.messageContent = content.replace('${0}', pageName);
    this.openDialogWithId("deletePageDialog");
    this.deleteParams = { 'pageName': pageName, 'pageType': 'page' };
  }

  onDeletePageConfirm(params: any) {
    let pageName = params.pageName,
      successCallback: any;
    if (this.isTemplateBundleType) {
      successCallback = this.onTemplateDeleteSuccess.bind(undefined, params, pageName);
    } else {
      successCallback = this.onPageDeleteSuccess.bind(undefined, params, pageName);
    }
    (this.pagesService.remove(pageName) as Promise<any>).then((response: any) => {
      this.pagesService.list().then(() => {
        successCallback();
        this.eventManager.emitupdateRouteHistory({name: pageName, type: "PAGE"});
      })
    });
    this.resourceMngr.removeUrlCache(pageName);
    // explicity closing dialog as it is not closed after the page deletion
    this.closeDialogWithId("deletePageDialog");
  }

  onPageOrTemplateDeleteSuccess(pageName: any) {
    //$rs.$emit('wms:post-delete-page', pageName);
    this.eventManager.emitUpdatePageTree();
    this.eventManager.emitReRenderCanvasContent({ pageName: "Main", isMarkupUpdated: true });
    runtimeEventManager.emit('loadPage', "Main");
  }

  onTemplateDeleteSuccess = (params: any, pageName: any) => {
    this.onPageOrTemplateDeleteSuccess(pageName);
    StudioUtils.updateTemplateConfig(ACTIONS.DELETE, pageName);
  }

  onPageDeleteSuccess = (params: any, pageName: any) => {
    this.onPageOrTemplateDeleteSuccess(pageName);
    /*delete the navigation variable with new notation */
    this.variableService.deleteVariable('goToPage_' + pageName);
    /*delete the navigation variable with old notation */
    this.variableService.deleteVariable('goToPage-' + pageName);
    this.variableService.deleteVariable("", pageName);
    (this.variableService.saveVariables(pageName) as Promise<any>).then((response: any) => {
      this.notificationService.show('success', this.locale.MESSAGE_SUCCESS_TITLE, this.locale.MESSAGE_SUCCESS_PAGE_DELETE.replace("${0}", pageName));
    });
  }

  _duplicatePage(pageName: any, pageType: any) {
    let caption = '',
      content = '';

    if (this.isTemplateBundleType) {
      caption = 'LABEL_CONFIRM_DUPLICATE_TEMPLATE';
      content = this.locale.MESSAGE_DUPLICATE_TEMPLATE;
    } else {
      if (pageType !== 'PAGE') { // partial
        caption = 'LABEL_CONFIRM_DUPLICATE_PARTIAL_PAGE';
        content = this.locale.MESSAGE_DUPLICATE_PARTIAL_PAGE;
      } else {
        caption = 'LABEL_CONFIRM_DUPLICATE_PAGE';
        content = this.locale.MESSAGE_DUPLICATE_PAGE;
      }
    }
    this.dialogParams = {
      dialogCaption: caption,
      dialogContent: content,
      pageName: pageName,
      pageType: pageType,
      action: 'DUPLICATE',
    };
    this.dialogService.open('renamePageDialog', {
      addNewWorkspace: () => {
        return (pageName: any, type: any) => {
          // pagesMeta[pageName] = toUpper(type);
          // $rs.$emit('open-ws', 'PAGE', pageName, 'PAGE:' + pageName, {'pageName': pageName, 'tab': 'DESIGN'} );
          // $rs.$emit('wms:set-widgets-group-active'); //Expand widgets panel by default on new page creation
        };
      }
    });
  }

  duplicatePage(event: any, pageName: string, pageType: string) {
    event.preventDefault();
    this.hideDropDowns();
    this._duplicatePage(pageName, pageType);
  }

  pageActions(index: number) {
    for (let i = 0; i < this.pages.length; i++) {
      if (i == index) {
        this.pageActionsDD[index] = !this.pageActionsDD[index]
      }
      else {
        this.pageActionsDD[i] = false
      }
    }
  }

  loadPrefabList($event: any, group: any) {

  }

  importPrefab(event: any, group?: any) {
    event.preventDefault();
    event.stopPropagation();
    this.dialogService.open('uploadResourcesDialog','prefabs');
  }



  openCreateNewPageDialog() {
    let boundVariableName,
      boundVariable,
      boundVariableType;

    if(this.activeWidget && this.activeWidget.binddataset){
      boundVariableName  = this.activeWidget.binddataset.replace('bind:', '').split('.')[1];
       boundVariable =   this.variableService.variableInstanceCollection[this.pageWsService.activePageName][boundVariableName];
       boundVariableType = '';
      if (boundVariable && stringStartsWith(boundVariable.package, 'com.') && !stringStartsWith(boundVariable.package, 'com.wavemaker.')) {
        boundVariableType = boundVariable.package;
      }
    }

    this.templateData = {
      template: 'template',
      variableType: boundVariableType
    }

    this.dialogService.open('addNewPageDialog')


  }

  toggleHiddenElements() {
    this.pageWsService.showHiddenElements = !this.pageWsService.showHiddenElements
    //update storage value based on header button toggle changes
    localStorage.setItem("showHiddenElements", this.pageWsService.showHiddenElements === true ? 'true' : 'false');
    this.showHideComponentsBasedOnMarkup();
  }

  //added support to show wm-hidden-overlay wrapper on hidden components having show=false attribute on DOM
  showHideComponentsBasedOnMarkup() {
    let markup, hiddenElements: any, registry = getWidgetRegistry();
    markup = this?.markupManager?.getMarkup();
    hiddenElements = markup?.find("[show='false']");
    hiddenElements?.each((elementIndex: any, element: any) => {
      let widgetId, widgetName, widgetType, widgetElement;
      widgetId = element.getAttribute("widgetId")
      widgetName = element.getAttribute("name");
      widgetType = element.getAttribute("widgetType");
      widgetElement = widgetName && registry?.get(widgetId)?.nativeElement;
      if (widgetElement) {
        this.pageWsService.showHiddenElements ? widgetElement.removeAttribute("hidden") : widgetElement.setAttribute('hidden', 'true');
        this.handleHideAndShowOverlay(this.pageWsService.showHiddenElements, widgetElement, widgetType, widgetId);
      }
    })
  }

  //supports to show and hide hidden elements, Params -> showHiddenElements (read value from storage), widgetElement (element on which show hide to be performend), widgetType (type of widget), widgetId (id of the widget)
  handleHideAndShowOverlay(showHiddenElements: Boolean, widgetElement: any, widgetType: string, widgetId: string) {
    var parentWrapperElement, $hiddenEleNode, $hiddenEl, $wrapper, isWizardOrProgressCircleWidget = (widgetType === 'wm-wizardstep' || widgetType === "wm-progress-circle");
    parentWrapperElement = this.getWidgetContainer(widgetElement, widgetType, widgetId);
    //adds wm-hidden-overlay overlay wrapper on parentWrapperElement of widget
    if (showHiddenElements) {
      $hiddenEleNode = $('<div class="wm-hidden-overlay ' + widgetType + '" title="' + this.locale.LABEL_HIDDEN_WIDGET + '"></span></div>');
      if (widgetType === "wm-picture") {
        $wrapper = $('<div class="picture-wrapper" widgetid="' + $(widgetElement).attr("widgetid") + '" name="' + $(widgetElement).attr("name") + '" widgettype="wm-picture"></div>')
        $(widgetElement).wrap($wrapper);
        $(".picture-wrapper").append($hiddenEleNode);
      } else {
        isWizardOrProgressCircleWidget ? $(parentWrapperElement).addClass('wm-hidden-overlay ' + widgetType) : $(parentWrapperElement).append($hiddenEleNode);
      }
    }
    //removes wm-hidden-overlay overlay wrapper on parentWrapperElement of widget
    else {
      if (widgetType === "wm-picture") {
        $wrapper = $(widgetElement).closest(".picture-wrapper");
        $($wrapper).find('.wm-hidden-overlay').remove();
        $(widgetElement).unwrap();
      } else {
        if (isWizardOrProgressCircleWidget) {
          $(parentWrapperElement).removeClass('wm-hidden-overlay ' + widgetType);
        } else {
          $hiddenEl = $(parentWrapperElement).find('.wm-hidden-overlay.' + widgetType);
          $hiddenEl.remove();
        }
      }
    }
  }

  //this function returns parent wrappper element of the widget where we need to append wm-hidden-overlay
  getWidgetContainer(widgetElement: any, widgetType: string, widgetId: string) {
    let parentWrapperElement, registry = getWidgetRegistry();
    switch (widgetType) {
      case 'wm-accordionpane':
        parentWrapperElement = $(widgetElement).find('.panel-heading');
        break;
      case 'wm-text':
      case 'wm-number':
      case 'wm-select':
      case 'wm-textarea':
        parentWrapperElement = $(widgetElement).closest('div');
        break;
      case 'wm-tabpane':
      case 'wm-wizardstep':
        let tabContentElement, tabParentElement, closestElement, selectorElement;
        //To find the closest & selector elements of the tabpane, wizard step
        if (widgetType === "wm-tabpane") {
          closestElement = ".app-tabs";
          selectorElement = "data-paneid";
        } else {
          closestElement = ".app-wizard";
          selectorElement = "data-wizardstepid"
        }
        closestElement = widgetType === "wm-tabpane" ? ".app-tabs" : ".app-wizard";
        tabContentElement = widgetId && registry.get(widgetId)?.nativeElement;
        tabParentElement = tabContentElement && $(tabContentElement)?.closest(closestElement)[0];
        parentWrapperElement = tabParentElement?.querySelector(`[${selectorElement}=${widgetId}]`);
        this.pageWsService.showHiddenElements ? parentWrapperElement.removeAttribute("hidden") : parentWrapperElement?.setAttribute('hidden', 'true');
        break;
      default:
        parentWrapperElement = $(widgetElement);
        break;
    }
    return parentWrapperElement;
  }

  undo() {
    this.pageWsService.undo(this.designContainerComp?.propPanel, this.designContainerComp?.canvasDomComponent);
  }

  redo() {
    this.pageWsService.redo(this.designContainerComp?.propPanel, this.designContainerComp?.canvasDomComponent);
  }

  showThemeDialog() {
    this.dialogService.open('themeDialog');
  }
  showTemplateMenu($event: any, show: any) {
    this.templateMenu['visible'] = show === undefined ? !this.templateMenu['visible'] : show;
    if ($($event.currentTarget).find('.iScrollVerticalScrollbar').length > 0) {
      this.templateMenu['iScroll']?.refresh();
      return;
    }
    setTimeout(() => {
      let scrollable = $($event.currentTarget).parent().find('.wm-template-list:first');
      let dismissOnOutsideClick = true;
      let onExternalClick = (event: any) => {
        if (!dismissOnOutsideClick || $(event.target).hasClass('wms-template-layout')) {
          return;
        }
        this.templateMenu['visible'] = false;
        $(event).off('click', onExternalClick);
        // $rs.$safeApply($s);
      };
      $(document).on('click', onExternalClick);
      if (this.templateMenu && this.templateMenu.iScroll) {
        this.templateMenu.iScroll.destroy();
      }
      // TODO
      // this.templateMenu['iScroll'] = new IScroll(scrollable[0], {
      //     'mouseWheel': true,
      //     'scrollbars': true,
      //     'click': true,
      //     'bounce': false
      // });
    }, 200);
  }

  loadWidgetTemplates(data: any) {
    let isTemplateBundled = data.isTemplateBundled, isLayoutBundled = data.isLayoutBundled, widgetType = data.widgetType;
    if (!isTemplateBundled && !isLayoutBundled) {
      this.templateMenu['visible'] = false;
      this.templateOptions = [];
      this.pageWsService.layoutOptions = [];
      return;
    }
    if (this.projectService.isPrism() && data?.widgetType && !this.templateSupportedWidgets.includes(data.widgetType)) {
      return;
    }
    if (isTemplateBundled) {
      if (!isEmpty(this.pageWsService.layoutOptions)) {
        this.templateMenu['visible'] = false;
        this.pageWsService.layoutOptions = [];
      }
      this.loadTemplates(widgetType);
    }
    if (isLayoutBundled) {
      if (!isEmpty(this.templateOptions)) {
        this.templateMenu.visible = false;
        this.templateOptions = [];
      }
      this.loadLayouts(widgetType);
    }
  }

  loadTemplates(widgetType: string) {
    // TODO
    let templatePlatformType: any = 'WEB';
    this.pageTemplate.list(true, templatePlatformType)
      .then((response: any) => {
        this.templateOptions = [];
        let allTemplates: any = [];
        if (Array.isArray(response)) {
          response.forEach((bundle) => {
            if (Array.isArray(bundle.templates)) {
              bundle.templates.forEach((template: any) => {
                template.bundleId = bundle.id;
                template.bundleName = bundle.name;
                template.versionId = bundle.versionId;
                allTemplates.push(template);
              });
            }
          });
        }
        if (widgetType === "wm-list" && this.markupManager.getActiveMarkupEl()?.hasClass("list-card")) {
          widgetType = "wm-list-card";
        }
        this.templateOptions = filter(allTemplates, { 'category': widgetType });
        let template = find(this.templateOptions, (template) => {
          return template.name === this.markupManager.getActiveMarkupEl()?.attr('template-name');
        });
        this.pageWsService.setActive(this.templateOptions, template);
      });
  }

  loadLayouts(widgetType: string) {
    this.pageWsService.layoutOptions = this.widgetTemplatesService.getLayouts(widgetType) || [];
    let activeMarkupElClass = this.markupManager.getActiveMarkupEl()?.attr('class');
    let layout = find(this.pageWsService.layoutOptions, (layout) => {
      return layout?.properties?.class === activeMarkupElClass || layout?.class === activeMarkupElClass;
    });
    this.pageWsService.setActive(this.pageWsService.layoutOptions, layout);
    this.templateMenu['visible'] = this.templateMenu['visible'];
  }




  copyBindings(from: any, to: any) {
    let $from = from.clone(), $to = $(to), navigation = from.attr('navigation'), selectedWidget = $to.children().first();
    let widgetName: any;
    this.bindingsFound = {};
    $from = $('<div>').append($from);
    selectedWidget.attr('name', from.attr('name'));
    if (navigation) {
      selectedWidget.attr('navigation', navigation);
    }
    each($from.find('[name]'), (el: any) => {
      let $this = $(el);
      widgetName = $this.attr('name');
      let attributes = el.attributes;
      $.each(attributes, (i, attrib) => {
        let name = attrib.name;
        let value = attrib.value;
        if (startsWith(value, 'bind:')) {
          $this.attr(name, value);
          this.bindingsFound[widgetName] = {};
          this.bindingsFound[widgetName][name] = value;
        }
      });

    });
    each($to.find('[name]'), (el: any) => {
      let $this = $(el);
      widgetName = $this.attr('name');
      forEach(this.bindingsFound[widgetName], (v, k) => {
        $this.attr(k, v);
      });
    });
    return $to;
  }

  selectTemplate($event: any, template: any) {
    $event.stopPropagation();
    if (template.selected) {
      return;
    }
    this.pageWsService.setActive(this.templateOptions, template);
    this.widgetTemplatesService.getMarkup(template.bundleId, template.id, template.versionId)
      .then((markup) => {
        markup = this.copyBindings(this.markupManager.getActiveMarkupEl(), markup);
        runtimeEventManager.emit('canvas-switch-template', {
          pageName: activePageName,
          markup: markup.html()
        });
      });
  }

  openChangeLayoutDialog() {
    this.dialogService.open('layoutOptions')
  }

  onDeviceTypeChange(device: any) {
    let enableFitScreen = device.id === 'fitscreen';
    if(this.designContainerComp) this.designContainerComp.fitScreen = enableFitScreen;
    this.fitScreen = enableFitScreen;
    this.pageWsService.canvasResize(device);
  }

  override ngOnDestroy() {
    this.pageWsService.stopAutoSave();
    this.pageWsService.isDirty();
    super.ngOnDestroy();
    // Here only turn off the runtimeEventManager events
    // TODO: like if there are specific to widget , then destroy them on pagewrapper (which will be triggered for every page navigation )
    runtimeEventManager.off();
    removeShadowdom();
  }

  onUpdate(val: boolean) {
    this.showCreateNewPage = val;
  }

  getPreference(val: string) {
    let preferences = this.preferenceService.get();
    return val ? get(preferences, val) : true
  }
  createNewPage($event: any) {
    this.dialogService.open('addNewPageDialog', {

      templateData: () => {
        return () => {
          if (!$event.templateData || !$event.templateData.template) {
            return {};
          }

          let elementScope = $event.templateData.elementScope;
          let template = $event.templateData.template;
          let boundVariableName, boundVariable, boundVariableType;
          if (elementScope && elementScope.binddataset) {
            boundVariableName = elementScope.binddataset.replace('bind:', '').split('.')[1];
            boundVariable = elementScope.viewParent.Variables[boundVariableName];
            boundVariableType = '';
            if (boundVariable && stringStartsWith(boundVariable.package, 'com.') && !stringStartsWith(boundVariable.package, 'com.wavemaker.')) {
              boundVariableType = boundVariable.package;
            }
          }

          return {
            template: template,
            variableType: boundVariableType
          };
        }
      },
      addNewWorkspace: () => {
        return (pageName: string, type: string) => {
          let pagesMeta: any = {}
          pagesMeta[pageName] = toUpper(type);
          // openWS('PAGE', pageName, 'PAGE:' + pageName, {'pageName': pageName, 'tab': 'DESIGN'});
          // $rs.$emit('wms:set-widgets-group-active'); //Expand widgets panel by default on new page creation
        };
      }
    }
    )
  }

  eventHandler(event: string, options: any, isVariableEvt: boolean = false) {
    let eventName: any = [], widgetType: any, widgetName, openScript: any, eventPrefix, args: any;
    if (!isVariableEvt) {
      widgetType = this.activeWidget.widgetType;
      widgetName = options.widgetName || this.activeWidget.name;
    }

    openScript = isDefined(options.openScript) ? options.openScript : true;
    eventPrefix = options.eventPrefix || (this.activeWidget.name + event.replace('on-', '')).trim().replace(/ /g, '_');
    args = options.args;

    let ctx = this.isPrefabType ? (this.pageWsService.activePageInfo?.type === 'PARTIAL' ? 'Partial.' : 'Prefab.') : this.pageWsService.activePageInfo?.type === 'PAGE' ? 'Page.' : 'Partial.';

    if (isArray(eventPrefix)) {
      eventPrefix.forEach(function (prefix) {
        if (prefix) {
          eventName.push(ctx + prefix);
        }
      });
    } else {
      eventName.push(ctx + eventPrefix);
    }

    this.readScriptContent(eventName, openScript, isVariableEvt, widgetType, args, options, event);
  }

  writeContent(data: any) {
    let extension: string = '',
      content: any = data.content;

    if (data.type === 'JAVASCRIPT') {
      extension = '.js';
    } else if (data.type === 'STYLE') {
      extension = '.css';
    } else if (data.type === 'MARKUP') {
      extension = '.html';
    };
    let fileLocalURL: string = "../../../pages/" + this.pageWsService.activePageName + '/' + this.pageWsService.activePageName + extension,
      $readFilePromise: Promise<any>;
    if (extension === '.html') {
      this.pageWsService.saveFileContent(fileLocalURL, content).then(() => {
      });
    } else {
      $readFilePromise = this.fileService.read(fileLocalURL);
      $readFilePromise.then(
        (result) => {
          let data: any = result;
          data = data + content;
          this.pageWsService.saveFileContent(fileLocalURL, data).then(() => {
          });
        });
    }
  }

  readScriptContent(eventName: any, openScript: boolean, isVariableEvt: boolean, widgetType: string, args: any, options: any, event: string) {
    let fileLocalURL: string = "../../../pages/" + this.pageWsService.activePageName + '/' + this.pageWsService.activePageName + '.js';
    let $scriptPromise: Promise<any>;
    if (isVariableEvt && options.variable.owner === 'App') {
      fileLocalURL = 'app.js';
      $scriptPromise = this.fileService.read(fileLocalURL);
    } else if (this.pageMinJSON?.script) {
      $scriptPromise = Promise.resolve(this.pageMinJSON.script);
    } else {
      $scriptPromise = this.fileService.read(fileLocalURL);
    }
    $scriptPromise.then(
      (result) => {
        let data: any = result,
          eventListener: any = '',
          eventNotExists,
          filteredEvents;

        filteredEvents = eventName.filter((name: any) => {
          return data.indexOf(name) === -1;
        });
        eventNotExists = filteredEvents && filteredEvents.length > 0;
        if (eventNotExists || (!eventNotExists && isVariableEvt)) {
          if (isVariableEvt) {
            let eventData = this.variableService.createVariableEventListeners(options.variable, data);
            eventName = eventData.eventName;
            if (eventNotExists)
              eventListener = eventData.eventListener;
          } else {
            eventListener = this.getEventListener(event, widgetType, filteredEvents, args);
            eventName = filteredEvents[0];
          }
          data = data + eventListener;
          this.pageWsService.editorOptions.value = data;
          this.lineComponent = {
            x: data.substring(0, data.indexOf(eventName)).split('\n').length + 1,
            y: (data.split('\n')[data.substring(0, data.indexOf(eventName)).split('\n').length].split("\t").length - 1) * 4
          };
          this.pageWsService.saveFileContent(fileLocalURL, data).then(() => {
            if(isVariableEvt && options.variable.owner !== 'App'){
              this.pageMinJSON.script = data;
            }
            this.notificationService.show("success", this.locale.MESSAGE_SUCCESS_TITLE, this.locale.MESSAGE_SUCCESS_FILE_WRITE_DESC)
            if (isVariableEvt) {
              triggerFn(options.onSuccess, { variable: options.variable });
            } else {
              triggerFn(options.onSuccess);
            }
            if (openScript) {
              if (!isVariableEvt || (isVariableEvt && options.variable.owner !== 'App')) {
                this.postPageScriptUpdate(eventName);
              } else {
                this.router.navigate(['/files', {
                  name: 'app.js',
                  relativePath: '/src/main/webapp/app.js',
                  path: '../project/src/main/webapp/app.js',
                  type: "file"
                }]);
              }
            }
          }
          ).catch(() => {
            this.notificationService.show("error", this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_FILE_WRITE_DESC)
          })
        } else {
          if (openScript) {
            eventName = eventName[0];
            this.postPageScriptUpdate(eventName);
          }
        }
      }).catch(() => {
        this.notificationService.show("error", this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_FILE_WRITE_DESC)
      })
  }

  /*This function constructs the event snippet*/
  constructEventListener(eventListener: any, name: any, args: any, isListenerEvent: any, comment: any) {
    var event = eventListener;
    if (isListenerEvent) {
      event += '\n$scope.$root.$on(' + name.replace('$scope.', '') + ', function' + args + ' { \n \n';
    } else {
      event += '\n' + name + ' = function ' + args + ' { \n\n';
    }
    if (comment) {
      event += '/*\n' +
        '* ' + comment + '\n' +
        '*/\n';
    }
    event = event + (isListenerEvent ? '});\n' : '};\n');
    return event;
  }

  getEventListener(event: any, widgetType: string, eventName: any, defaultArgs: any) {
    let eventListener = '', args: any, isListenerEvent: any, comment: any;
    defaultArgs = defaultArgs || [];
    /*if the event is a success/error callback, bind emit event with the rootScope*/
    if ((["wm-table", "wm-liveform", "wm-form", "wm-fileupload", "wm-logindialog", "wm-login", "wm-camera", "wm-barcodescanner", "wm-livefilter"].indexOf(widgetType) <= -1) && (event === 'on-Success' || event === 'on-Error')) {
      args = '($event)';
      isListenerEvent = true;
    } else if ((widgetType === "wm-login" || widgetType === "wm-logindialog") && event === 'on-Error') {
      args = '($event, widget)';
      comment = 'Error message can be accessed from the property widget.loginMessage.caption';
    } else if (widgetType === "wm-chart" && event === 'on-Select') {
      args = '($event, widget, selectedItem, selectedChartItem)';
    } else if (widgetType === 'wm-wizard' && (includes(['on-Done', 'on-Cancel'], event))) {
      args = '(widget, steps)';
    } else if (widgetType === 'wm-wizardstep') {
      if (includes(['on-Next', 'on-Prev', 'on-Skip'], event)) {
        args = '(widget, currentStep, stepIndex)';
      } else if (event === 'on-Load') {
        args = '(widget, stepIndex)';
      }
    } else if (widgetType === 'wm-list' && event === 'groupby') {
      args = '(row)';
      comment = 'this function is iterated over each data object in the livelist dataSet collection the data will be grouped by what'
        + ' is returned from this function E.g. to group a collection of CGPA details under rounded figure CGPA return following '
        + 'return Math.floor(dataObject.cgpa)';
    } else if (widgetType === 'wm-carousel' && event === 'on-Change') {
      args = this.getListenerArguments(event, widgetType, 'widget');
      comment = 'newIndex: index of the active carousel in view \n' +
        '\t\t\t* oldIndex: index of the previous carousel in view \n' +
        '\t\t\t* Note: In case carousels are rendered dynamically through binding dataSet, use following properties to access carousel data objects:\n' +
        '\t\t\t*   -widget.currentslide: data object against the active carousel in view \n' +
        '\t\t\t*   -widget.previousslide: data object against the previous carousel in view';
    } else {
      args = this.getListenerArguments(event, widgetType, 'widget');
    }
    forEach(eventName, (name: any, index: any) => {
      eventListener = this.constructEventListener(eventListener, name, defaultArgs[index] || args, isListenerEvent, comment);
    });
    return eventListener;
  }

  getListenerArguments(event: string, widgetType: string, scope?: any) {
    let args,
      gridRowEvents,
      gridRowBeforeEvents;

    if (this.activeWidget && (widgetType === 'wm-form-field' || widgetType === 'wm-filter-field')) {
      widgetType = this.liveWidgetUtilsService.getFormWidgetType(this.activeWidget.displaywidget);
    }

    scope = scope || 'widget';
    switch (event) {
      case 'on-Actionsclick':
        args = '$item';
        break;
      case 'liveWidget-action':
        args = '$event';
        break;
      case 'on-Resize':
      case 'on-Orientationchange':
        args = '$event, widget, data';
        break;
      case 'on-Backbtnclick':
        args = '$event, ' + scope;
        break;
      default:
        switch (widgetType) {
          case 'wm-menu':
          case 'wm-mobile-tabbar':
            args = '$event, ' + scope + ', $item';
            break;
          case 'wm-tree':
            args = '$event, ' + scope + ', $item, $path';
            break;
          case 'wm-pagination':
            if (event === 'on-Paginationchange') {
              args = '$event, ' + scope + ', $index';
            } else {
              args = '$event, ' + scope + ', $data, $index';
            }
            break;
          case 'wm-fileupload':
            if (event === 'on-Select') {
              args = '$event, ' + scope + ', selectedFiles';
            } else if (event === 'on-Beforeselect' || event === 'on-Error') {
              args = '$event, ' + scope + ', files';
            } else if(event === 'on-Beforedelete' || event === 'on-Delete' ) {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-search':
            if (event === 'on-Select') {
              args = '$event, ' + scope + ', selectedValue';
            } else if (event === 'on-Beforeservicecall') {
              args = scope + ', inputData';
            } else if (event === 'on-Datasetready') {
              args = scope + ', data';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-nav':
            args = '$event, ' + scope + ', $item';
            break;
          case 'wm-calendar':
            if (event === 'on-Eventresize') {
              args = '$event, $newData, $oldData, $delta, $revertFunc, $ui, $view';
            } else if (event === 'on-Eventdrop') {
              args = '$event, $newData, $oldData, $delta, $revertFunc, $ui, $view';
            } else if (event === 'on-Eventclick') {
              args = '$event, $data, $view';
            } else if (event === 'on-Beforerender') {
              args = '$event, ' + scope;
            } else if (event === 'on-Eventrender') {
              args = '$event, $data, $view';
            } else if (event === 'on-Viewrender') {
              args = '$view';
            } else if (event === 'on-Select') {
              args = '$start, $end, $view, $data';
            }
            break;
          case 'wm-form':
            if (event === 'on-Beforerender') {
              args = '$metadata, ' + scope;
            } else if (event === 'on-Submit') {
              args = '$event, ' + scope + ', $formData';
            } else {
              args = '$event, ' + scope + ', $data';
            }
            break;
          case 'wm-table':
            gridRowEvents = ['on-Rowselect', 'on-Rowdeselect', 'on-Rowclick', 'on-Rowdblclick', 'on-Rowdelete', 'on-Rowinsert', 'on-Rowupdate'];
            gridRowBeforeEvents = ['on-Beforerowinsert', 'on-Beforerowupdate', 'on-Beforerowdelete'];
            if (event === 'on-Paginationchange' || event === 'on-Setrecord') {
              args = '$event, ' + scope + ', data, pageInfo';
            } else if (event === 'on-Headerclick') {
              args = '$event, ' + scope + ', column';
            } else if (event === 'on-Beforedatarender') {
              args = scope + ', data, columns';
            } else if (event === 'on-Datarender') {
              args = scope + ', data';
            } else if (event === 'on-Show' || event === 'on-Hide') {
              args = '';
            } else if (event === 'on-Beforeformrender') {
              args = '$event, ' + scope + ', row, $operation';
            } else if (event === 'on-Formrender') {
              args = '$event, ' + scope + ', formWidgets, $operation';
            } else if (event === 'on-Error') {
              args = '$event, ' + scope + ', $data, $operation';
            } else if (event === 'on-Beforeexport') {
              args = scope + ', $data';
            } else if (event === 'on-Beforefilter') {
              args = scope + ', columns';
            } else if (event === 'on-Tap' || event === 'on-Click') {
              args = '$event, widget';
            } else if (includes(gridRowBeforeEvents, event)) {
              args = '$event, ' + scope + ', row, options';
            } else if (includes(gridRowEvents, event)) {
              args = '$event, ' + scope + ', row';
            } else {
              args = '$event, ' + scope + ', $data';
            }
            break;
          case 'wm-livefilter':
            args = '$data';
            break;
          case 'wm-liveform':
            if (event === 'on-Beforeservicecall') {
              args = '$event, $operation, $data, options';
            } else {
              args = '$event, $operation, $data';
            }
            break;
          case 'wm-progress-bar':
            if (event === 'on-Start' || event === 'on-Complete') {
              args = scope;
            } else if (event === 'on-Tooltiptext') {
              args = '$event, widget, minVal, maxVal, percentage'
            } else {
              args = '$response, ' + scope;
            }
            break;
          case 'wm-prefab':
            args = '$event, $data';
            break;
          case 'wm-segmented-control':
            args = scope + ', $old, $new';
            break;
          case 'wm-chart':
            if (event === 'on-Select') {
              args = '$event, ' + scope + ', selectedItem, selectedChartItem';
            } else if (event === 'on-Beforerender') {
              args = scope + ', chartInstance';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-wizard':
            if (includes(['on-Done', 'on-Cancel'], event)) {
              args = scope + ', steps';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-wizardstep':
            if (includes(['on-Next', 'on-Prev', 'on-Skip'], event)) {
              args = scope + ', currentStep, stepIndex';
            } else if (event === 'on-Load') {
              args = scope + ', stepIndex';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-list':
            if (event === 'on-Reorder') {
              args = '$event, $data, $changedItem';
            } else if (event === 'groupby') {
              args = 'row';
            } else if (event === 'on-Paginationchange') {
              args = '$event, ' + scope + ', $index';
            } else if (includes(['on-Beforedatarender', 'on-Render', 'on-Select'], event)) {
              args = scope + ', $data';
            } else if (event === 'on-Setrecord') {
              args = '$event, ' + scope + ', $index, $data';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-carousel':
            args = scope;
            break;
          case 'wm-breadcrumb':
            args = scope + ', $item';
            break;
          case 'wm-panel':
          case 'wm-popover':
          case 'wm-container':
          case 'wm-pagedialog':
          case 'wm-tabcontent':
          case 'wm-accordionpane':
            if (event === 'on-Load') {
              args = scope;
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-tabpane':
            if (event === 'on-Headerclick') {
              args = '$event, ' + scope + ', paneIndex';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-camera':
            if (event === 'on-Success') {
              args = '$event, ' + scope + ', localFilePath, localFile';
            } else {
              args = '$event, ' + scope;
            }
            break;
          case 'wm-chips':
            if (event === 'on-Reorder' || event === 'on-Beforereorder') {
              args = '$event, ' + scope + ', $data, $changedItem';
            } else if (event === 'on-Beforeadd') {
              args = '$event, ' + scope + ', newItem';
            } else if (event === 'on-Change') {
              args = '$event, ' + scope;
            } else if (event === 'on-Beforeservicecall') {
              args = scope + ', inputData';
            } else {
              args = '$event, ' + scope + ', $item';
            }
            break;
          default:
            args = '$event, ' + scope;
        }
    }

    if (event === 'groupby') {
      args = 'row';
    }

    //If parent is list template add item as 3rd argument and currentItemWidgets as 4th arguments
    if (this.markupManager.getActiveMarkupEl()?.closest('wm-listtemplate').length) {
      args += ', item, currentItemWidgets';
    }
    //If event is on-Change newVal, oldVal as 3rd and 4th arguments
    if (event === 'on-Change') {
      if (includes(['wm-tabs', 'wm-accordion'], widgetType)) {
        args += ', newPaneIndex, oldPaneIndex';
      } else if (widgetType === 'wm-carousel') {
        args += ', newIndex, oldIndex';
      } else {
        args += ', newVal, oldVal';
      }
    }
    return '(' + args + ')';
  }

  private postPageScriptUpdate(fnName: string) {
    let newfnName = fnName.concat(" = function");
    this.switchTab(TABS.SCRIPT, { tab: TABS.SCRIPT }).then((): void => {
      setTimeout(async () => {
        const isEditorAvailable = await this.editorComponent.ensureMonacoEditorLoaded();
        if(isEditorAvailable) {
          let wmsEditor = this.editorComponent.wmsEditor;
          this.editorComponent.setEditorFocus();
          let wmsEditorModel: ITextModel | null = wmsEditor?.getModel();

          let position: FindMatch | null | undefined = wmsEditorModel?.findNextMatch(newfnName, { column: 1, lineNumber: 1 }, false, true, null, true);
          if (position?.matches?.length) {
            let lineNumber: number = position?.range.startLineNumber + 1;
            let editorPos: IPosition = {
              lineNumber: lineNumber || 1,
              column: 1
            };
            this.editorComponent.setEditorFocus();
            this.editorComponent.setCursorPosition(editorPos);
          } else {
            this.notificationService.show('info', '', 'Unable to navigate to selected method ' + fnName)
          }
        }
      })
    });
  }

  handleKeyboardResourceSave = (event: KeyboardEvent): void => {
    if (event.ctrlKey && event.key == "s") {
      event.preventDefault();
      this.save();
    }
  }

  pageStructureTreeInit() {
    // Only for the first time initialization, setting the activeElement on tree.
    if (this.enablePageStructureActiveNode) {
      this.eventManager.emitOnCanvasUpdate({ eventType: 'ACTIVE' });
    }
    this.enablePageStructureActiveNode = false;
  }



  setCursorPosition(data: any){
    this.editorComponent.setEditorFocus();
    this.editorComponent.setCursorPosition(data);
  }

  openDialogForInvalidRepoBranch(invalidRepoBranchMessage: string) {
    const dialogs: any = {
      'REPOSITORY_NOT_FOUND': 'missingRepoDialog',
      'BRANCH_NOT_FOUND': 'missingBranchDialog',
      'USER_VCS_CREDENTIALS_REQUIRED': 'missingCredentialsDialog'
    };
    let dialogId: string = dialogs[invalidRepoBranchMessage];

    if (dialogId === 'missingCredentialsDialog' && this.projectService.projectEdnDetails.vcsAccountDTO.developerKeysEnabled) {
      this.vcsService.showUserVCSCredentialsDialog('', 'USER_VCS_CREDENTIALS_REQUIRED')
    } else {
      this.openDialogWithId(dialogId);
    }
  }

  getIconUrl() {
    return getVcsAccountIconUrl(this.projectService.projectEdnDetails.vcsAccountDTO.vcsAccountType, this.vcsService.baseURL)
  }
  getGroupData() {
    let groupData = getVCSGroupData(this.projectService.projectEdnDetails.vcsAccountDTO.vcsAccountType, this.projectService.projectEdnDetails.vcsAccountDTO.properties);
    this.vcsGroupName = groupData.groupName;
    this.vcsGroupLabel = groupData.groupLabel;
    return this.vcsGroupName;
  }
}
